import React, { useState, useEffect } from 'react';
import { Modal, FormInput, FormSelect, SearchableSelect } from '@/components/ui';
import { fmt } from '@/lib/formatters';
import { Wallet, CheckCircle2, CreditCard, Landmark, Banknote, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export const CreditPaymentForm = ({ isOpen, onClose, onConfirm, projects = [], targetStatus }) => {
    const [activeTab, setActiveTab] = useState('credit');
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        relatedProjectId: '',
        relatedProjectCollection: '',
        stage: 'milestone',
        paymentDate: new Date().toISOString().split('T')[0],
        amount: '',
        method: 'bank_transfer',
        transactionId: '',
        bankName: '',
        branch: '',
        upiPhone: '',
        receiverName: '',
        receiverPhone: '',
        chequeNumber: '',
        chequeDate: '',
        ifscCode: '',
        remarks: ''
    });

    const [followupForm, setFollowupForm] = useState({
        date: new Date().toISOString().split('T')[0],
        approxPaymentDate: '',
        reason: '',
        description: ''
    });

    useEffect(() => {
        if (isOpen) {
            setActiveTab('credit');
            setForm({
                relatedProjectId: '',
                relatedProjectCollection: '',
                stage: targetStatus === 'advance_payment_10' ? 'advance_10' :
                       targetStatus === 'advance_payment_50' ? 'advance_50' : 
                       targetStatus === 'final_payment' ? 'final' : 'advance_10',
                paymentDate: new Date().toISOString().split('T')[0],
                amount: '',
                method: 'bank_transfer',
                transactionId: '',
                bankName: '',
                branch: '',
                upiPhone: '',
                receiverName: '',
                receiverPhone: '',
                chequeNumber: '',
                chequeDate: '',
                ifscCode: '',
                remarks: ''
            });
            setFollowupForm({
                date: new Date().toISOString().split('T')[0],
                approxPaymentDate: '',
                reason: '',
                description: ''
            });
            
            if (projects.length === 1) {
                handleProjectSelect(projects[0]);
            }
        }
    }, [isOpen, projects, targetStatus]);

    const handleProjectSelect = (p) => {
        setForm(prev => ({
            ...prev,
            relatedProjectId: p._id || p.projectId,
            relatedProjectCollection: p.model || 'VerveContact'
        }));
    };

    const handleSubmit = async () => {
        const payAmount = Number(form.amount);
        if (!form.relatedProjectId) {
            return toast.error("Please select a Project");
        }
        if (!payAmount || payAmount <= 0) {
            return toast.error("Please enter a valid payment amount");
        }
        if (!form.stage) {
            return toast.error("Please select a payment type");
        }
        if (!form.paymentDate) {
            return toast.error("Please enter a transaction date");
        }
        /* Developer : Nithin - Date : 21-07-2026 - Added 10-digit phone number and mandatory Receiver Name validations for UPI and Cash */
        if (form.method === 'upi_qr' && (!form.upiPhone || !/^\d{10}$/.test(form.upiPhone.trim()))) return toast.error("Please enter a valid 10-digit UPI Phone Number");
        if (form.method === 'cash' && (!form.receiverName || !form.receiverName.trim())) return toast.error("Please enter Receiver Name");
        if (form.method === 'cash' && (!form.receiverPhone || !/^\d{10}$/.test(form.receiverPhone.trim()))) return toast.error("Please enter a valid 10-digit Receiver Phone Number");

        setLoading(true);
        try {
            if (onConfirm) {
                await onConfirm(form);
            } else {
                toast.success("Received amount recorded successfully!");
                onClose();
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to record payment");
        } finally {
            setLoading(false);
        }
    };

    const handleFollowupSubmit = async () => {
        if (!followupForm.date) {
            return toast.error("Please enter the date");
        }
        if (!followupForm.reason) {
            return toast.error("Please enter the reason for non-payment");
        }
        
        setLoading(true);
        try {
            if (onConfirm) {
                await onConfirm({
                    ...form,
                    isFollowup: true,
                    followupReason: followupForm.reason,
                    approxPaymentDate: followupForm.approxPaymentDate,
                    followupDescription: followupForm.description,
                    paymentDate: followupForm.date,
                    amount: 0,
                    method: 'none'
                });
            } else {
                toast.success("Payment follow-up recorded successfully!");
                onClose();
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to record follow-up");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div className="flex flex-col gap-0.5">
                    <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
                        Verve Kitchens
                    </span>
                    <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
                        Add Received Amount
                    </span>
                </div>
            }
            size="max-w-md"
        >
            <div className="space-y-2">
                {/* Header Info */}
                <div className="p-2 bg-blue-50/50 rounded-xl border border-blue-100 flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-brand-blue flex-shrink-0">
                        <Wallet size={16} />
                    </div>
                    <div>
                        <h4 className="text-[11px] font-bold text-blue-900 tracking-wider uppercase">
                            {{
                                "advance_10": "Advance Payment",
                                "advance_50": "Advance Payment",
                                "final": "Final Payment",
                                "others": "Other Payment"
                            }[form.stage] || "Payment Transactions"}
                        </h4>
                        <p className="text-[10px] font-medium text-brand-blue uppercase">
                            {targetStatus === 'advance_payment_10' 
                                ? "Record Client Payment or Follow-up" 
                                : "Record Client Payment"}
                        </p>
                    </div>
                </div>

                {/* Project Financial Summary */}
                {(() => {
                    const selectedProject = projects.find(p => (p._id || p.projectId) === form.relatedProjectId);
                    if (!selectedProject) return null;
                    
                    const getNum = (v) => {
                        if (typeof v === 'string') {
                            const parsed = Number(v.replace(/[^0-9.-]+/g,""));
                            return isNaN(parsed) ? 0 : parsed;
                        }
                        const n = Number(v);
                        return isNaN(n) ? 0 : n;
                    };

                    const projectAmount = getNum(selectedProject.quotationAmount) 
                        || getNum(selectedProject.initialQuotationData?.totals?.grandTotal) 
                        || getNum(selectedProject.initialQuotationData?.quotationAmount) 
                        || getNum(selectedProject.projectValue) 
                        || getNum(selectedProject.budget) 
                        || 0;
                        
                    const totalPaid = getNum(selectedProject.totalReceived);
                    const balance = projectAmount > 0 ? (projectAmount - totalPaid) : 0;

                    if (projectAmount <= 0 && totalPaid <= 0) return null;

                    return (
                        <div className="grid grid-cols-3 gap-2 mt-2 mb-1">
                            <div className="bg-slate-50 border border-slate-200 p-2 rounded-lg flex flex-col items-center justify-center">
                                <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Project Value</span>
                                <span className="text-[12px] text-slate-700 font-bold">₹{fmt(projectAmount)}</span>
                            </div>
                            <div className="bg-emerald-50 border border-emerald-200 p-2 rounded-lg flex flex-col items-center justify-center">
                                <span className="text-[9px] text-emerald-600 font-bold uppercase tracking-wider mb-0.5">Paid Amount</span>
                                <span className="text-[12px] text-emerald-700 font-bold">₹{fmt(totalPaid)}</span>
                            </div>
                            <div className="bg-orange-50 border border-orange-200 p-2 rounded-lg flex flex-col items-center justify-center">
                                <span className="text-[9px] text-orange-600 font-bold uppercase tracking-wider mb-0.5">Balance Due</span>
                                <span className="text-[12px] text-orange-700 font-bold">₹{fmt(balance)}</span>
                            </div>
                        </div>
                    );
                })()}

                {/* Tabs - Only show for advance_payment_10 */}
                {targetStatus === 'advance_payment_10' && (
                    <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-100 mt-2">
                        <button
                            type="button"
                            onClick={() => setActiveTab('credit')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${
                                activeTab === 'credit'
                                    ? 'bg-white text-blue-600 shadow-sm border border-blue-100'
                                    : 'text-slate-400 hover:text-slate-600'
                            }`}
                        >
                            <ArrowDownCircle size={14} /> Credit (Money In)
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('followup')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${
                                activeTab === 'followup'
                                    ? 'bg-red-50 text-red-600 shadow-sm border border-red-100'
                                    : 'text-slate-400 hover:text-slate-600'
                            }`}
                        >
                            <ArrowUpCircle size={14} /> Payment Follow-up
                        </button>
                    </div>
                )}

                {activeTab === 'credit' || targetStatus !== 'advance_payment_10' ? (
                    <div className="space-y-2 animate-in fade-in duration-300">
                        <div className="grid grid-cols-2 gap-2 items-start">
                            <FormInput
                                label="Project details *"
                                value={projects.length > 0 
                                    ? (() => {
                                        const p = projects.find(proj => (proj._id || proj.projectId) === form.relatedProjectId) || projects[0];
                                        return `${p.customerName || p.firstName || "Unknown Customer"} — ${p.customerCustomId || p.projectId || p.customId || ""}`;
                                    })()
                                    : "No Project Selected"}
                                readOnly
                                disabled
                                className="bg-slate-50 text-slate-500 font-medium"
                            />
                            <FormInput
                                label="Transaction Date *"
                                type="date"
                                value={form.paymentDate}
                                onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <FormInput
                                label="Amount Received (₹) *"
                                type="number"
                                placeholder="0.00"
                                value={form.amount}
                                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                            />
                            <FormSelect
                                label="Payment type *"
                                value={form.stage}
                                onChange={(e) => setForm({ ...form, stage: e.target.value })}
                                options={(() => {
                                    if (targetStatus === 'advance_payment_50') {
                                        return [
                                            { value: "advance_50", label: "Advance Payment" },
                                            { value: "others", label: "Other Payment" }
                                        ];
                                    } else if (targetStatus === 'final_payment') {
                                        return [
                                            { value: "final", label: "Final Payment" },
                                            { value: "others", label: "Other Payment" }
                                        ];
                                    }
                                    return [
                                        { value: "advance_10", label: "Advance Payment" },
                                        { value: "others", label: "Other Payment" }
                                    ];
                                })()}
                            />
                        </div>

                        <div>
                            <label className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Payment Mode</label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {[
                                    { id: 'bank_transfer', label: 'Bank', icon: <Landmark size={14} /> },
                                    { id: 'upi_qr', label: 'UPI', icon: <CreditCard size={14} /> },
                                    { id: 'cash', label: 'Cash', icon: <Banknote size={14} /> },
                                    { id: 'cheque', label: 'Cheque', icon: <CheckCircle2 size={14} /> }
                                ].map(m => (
                                    <button
                                        key={m.id}
                                        type="button"
                                        onClick={() => setForm({ ...form, method: m.id })}
                                        className={`flex flex-col items-center justify-center gap-1 py-1.5 rounded-xl border-2 transition-all ${
                                            form.method === m.id 
                                            ? 'bg-brand-blue/5 border-brand-blue text-brand-blue shadow-sm' 
                                            : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                                        }`}
                                    >
                                        {m.icon}
                                        <span className="text-[9px] font-bold uppercase tracking-wider">{m.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Dynamic Fields */}
                        {form.method === 'bank_transfer' && (
                            <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                <FormInput 
                                    label="Transaction / Ref ID"
                                    placeholder="TRX-123..."
                                    value={form.transactionId}
                                    onChange={(e) => setForm({ ...form, transactionId: e.target.value })}
                                />
                                <FormInput 
                                    label="Bank Name"
                                    placeholder="HDFC, SBI..."
                                    value={form.bankName}
                                    onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                                />
                                <FormInput 
                                    label="Branch"
                                    placeholder="Branch Name"
                                    value={form.branch}
                                    onChange={(e) => setForm({ ...form, branch: e.target.value })}
                                />
                                <FormInput 
                                    label="IFSC Code"
                                    placeholder="IFSC Code"
                                    value={form.ifscCode}
                                    onChange={(e) => setForm({ ...form, ifscCode: e.target.value })}
                                />
                            </div>
                        )}

                        {form.method === 'upi_qr' && (
                            <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                <FormInput 
                                    label="UPI UTR / Ref No."
                                    placeholder="TRX-123..."
                                    value={form.transactionId}
                                    onChange={(e) => setForm({ ...form, transactionId: e.target.value })}
                                />
                                <FormInput 
                                    label="UPI Phone Number"
                                    required={true}
                                    maxLength={10}
                                    placeholder="10-digit number"
                                    value={form.upiPhone}
                                    onChange={(e) => setForm({ ...form, upiPhone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                />
                            </div>
                        )}

                        {form.method === 'cash' && (
                            <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                <FormInput 
                                    label="Receiver Name"
                                    required={true}
                                    placeholder="Who received"
                                    value={form.receiverName}
                                    onChange={(e) => setForm({ ...form, receiverName: e.target.value })}
                                />
                                <FormInput 
                                    label="Receiver Phone"
                                    required={true}
                                    maxLength={10}
                                    placeholder="10-digit number"
                                    value={form.receiverPhone}
                                    onChange={(e) => setForm({ ...form, receiverPhone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                />
                            </div>
                        )}

                        {form.method === 'cheque' && (
                            <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                <FormInput 
                                    label="Cheque Number"
                                    placeholder="6-digit No."
                                    value={form.chequeNumber}
                                    onChange={(e) => setForm({ ...form, chequeNumber: e.target.value })}
                                />
                                <FormInput 
                                    label="Cheque Date"
                                    type="date"
                                    value={form.chequeDate}
                                    onChange={(e) => setForm({ ...form, chequeDate: e.target.value })}
                                />
                                <FormInput 
                                    label="Issuing Bank"
                                    placeholder="Bank name"
                                    value={form.bankName}
                                    onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                                />
                                <FormInput 
                                    label="IFSC Code"
                                    placeholder="IFSC Code"
                                    value={form.ifscCode}
                                    onChange={(e) => setForm({ ...form, ifscCode: e.target.value })}
                                />
                            </div>
                        )}

                        <div>
                            <label className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Internal Remarks</label>
                            <input
                                type="text"
                                className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all"
                                placeholder="Optional notes..."
                                value={form.remarks}
                                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                            />
                        </div>

                        {/* Footer Actions */}
                        <div className="flex gap-2 pt-2 mt-2 border-t border-slate-100">
                            <button
                                className="flex-1 py-2 border border-slate-200 rounded-lg text-[11px] text-slate-600 font-bold hover:bg-slate-50 transition-colors"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Discard
                            </button>
                            <button
                                className="flex-1 py-2 bg-slate-900 hover:bg-black text-white rounded-md text-[11px] font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                                onClick={handleSubmit}
                                disabled={loading || !form.amount || Number(form.amount) <= 0}
                            >
                                {loading ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <>
                                        <CheckCircle2 size={14} />
                                        Record Payout
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3 animate-in fade-in duration-300 mt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <FormInput
                                label="Date *"
                                type="date"
                                value={followupForm.date}
                                onChange={(e) => setFollowupForm({ ...followupForm, date: e.target.value })}
                            />
                            <FormInput
                                label="Approx. Payment Date"
                                type="date"
                                value={followupForm.approxPaymentDate}
                                onChange={(e) => setFollowupForm({ ...followupForm, approxPaymentDate: e.target.value })}
                            />
                        </div>
                        <FormInput
                            label="Reason for Non Payment *"
                            placeholder="e.g. Customer out of station"
                            value={followupForm.reason}
                            onChange={(e) => setFollowupForm({ ...followupForm, reason: e.target.value })}
                        />
                        <div>
                            <label className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Description</label>
                            <textarea
                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-red-500/20 focus:border-red-500 outline-none transition-all resize-none h-[72px]"
                                placeholder="Additional details..."
                                value={followupForm.description}
                                onChange={(e) => setFollowupForm({ ...followupForm, description: e.target.value })}
                            />
                        </div>

                        {/* Footer Actions */}
                        <div className="flex gap-2 pt-2 mt-2 border-t border-slate-100">
                            <button
                                className="flex-1 py-2 border border-slate-200 rounded-lg text-[11px] text-slate-600 font-bold hover:bg-slate-50 transition-colors"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Discard
                            </button>
                            <button
                                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-[11px] font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                                onClick={handleFollowupSubmit}
                                disabled={loading || !followupForm.reason || !followupForm.date}
                            >
                                {loading ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <>
                                        <ArrowUpCircle size={14} />
                                        Record Payment Follow-up
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};
